/* Core & libraries */
import React, {useEffect, useState, useRef} from "react";
import { useTheme } from "react-jss";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Flatpickr from "react-flatpickr";
import { Row, Col } from "react-bootstrap";
import Popover from "react-bootstrap/Popover";
import moment from "helpers/moment";
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/pt';
import "moment/locale/de";
import "moment/locale/it";
import "moment/locale/ja";
import "moment/locale/zh-cn";
import "moment/locale/cs";
import "moment/locale/nb";
import "moment/locale/sv";
import "moment/locale/ko";
import "moment/locale/ar";
import "moment/locale/pl";
import 'moment/locale/pt-br';

/* Helpers, constants, custom methods, internal libraries */
import UCOutsideClickHandler from "../ucOusideClickHandler/UCOutsideClickHandler";
import { getCalendarLocale } from '../../helpers/general';

/* Components */
import {ReactComponent as CalendarIcon} from "../../resources/images/calendar.svg";
import UCButton from "../buttons";
import UCSelect from "../ucselect";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useLocaleContext } from "../../contexts/localeContext";

/* Styles */
import styles, {dateSelectOpen} from "./styles.js"

const selectCalendarMobileOptions = [
  { value: 'allTime', label: <FormattedMessage id="bookings.filters.allTime" /> },
  { value: 'lastYear', label: <FormattedMessage id="bookings.filters.lastYear" /> },
  { value: 'year', label: <FormattedMessage id="bookings.filters.year" /> },
  { value: 'yearToDate', label: <FormattedMessage id="bookings.filters.yearToDate" /> },
  { value: 'lastMonth', label: <FormattedMessage id="bookings.filters.lastMonth" /> },
  { value: 'month', label: <FormattedMessage id="bookings.filters.thisMonth" /> },
  { value: 'lastWeek', label: <FormattedMessage id="bookings.filters.lastWeek" />},
  { value: 'week', label: <FormattedMessage id="bookings.filters.thisWeek" /> },
  { value: 'yesterday', label: <FormattedMessage id="bookings.filters.yesterday" /> },
  { value: 'today', label: <FormattedMessage id="bookings.filters.today" /> },
]

const messages = defineMessages({
  popoverAriaLabel: {
    id: "bookings.popoverAriaLabel"
  },
  prevBtnAriaLabel: {
    id: "bookings.prevBtnAriaLabel"
  },
  nextBtnAriaLabel: {
    id: "bookings.nextBtnAriaLabel"
  },
})

const SelectBookingDates = ({gridOptions, setGridOptions, defaultText, defaultValue, id, showOverlay, setShowOverlay}) => {
  const theme = useTheme();
  const classes = styles({ theme });
  const isMobile = useIsMobile();
  const [startDate, setStartDate] = useState(gridOptions?.startDate && gridOptions?.type === "custom" ? gridOptions?.startDate : null)
  const [endDate, setEndDate] = useState(gridOptions?.endDate && gridOptions?.type === "custom" ? gridOptions?.endDate : null)
  const dateFormat = "MMM DD, YYYY";
  const [appliedPlaceholder, setAppliedPlaceholder] = useState(defaultText)
  const { formatMessage } = useIntl();
  const [placeholder, setPlaceholder] = useState(defaultText)
  const [typeOption, setTypeOption] = useState({})
  const [type, setType] = useState(defaultValue)
  const globalLocale = useLocaleContext();
  const [startDateInitial, setStartDateInitial] = useState(gridOptions?.startDate && gridOptions?.type === "custom" ? gridOptions?.startDate : null)
  const [endDateInitial, setEndDateInitial] = useState(gridOptions?.endDate && gridOptions?.type === "custom" ? gridOptions?.endDate : null)

  useEffect(() => {
    if (type === defaultValue) {
      setPlaceholder(defaultText);
      setAppliedPlaceholder(defaultText);
    }
  }, [defaultText]);

  const applyButtonDates = (type) => {
    let range = {};
    let title = ""
    switch (type) {
      case "allTime":
        title = <FormattedMessage id="bookings.filters.allTime" />
        range = {
          startDate: "",
          endDate:"",
        }
        break;
      case "lastYear":
        title = <FormattedMessage id="bookings.filters.lastYear" />
        range = {
          startDate: moment().subtract(1, "year").startOf('year').format("YYYY-MM-DD"),
          endDate: moment().subtract(1,'year').endOf('year').format("YYYY-MM-DD"),
        }
        break;
      case "year":
        title = <FormattedMessage id="bookings.filters.year" />
        range = {
          startDate: moment().startOf('year').format("YYYY-MM-DD"),
          endDate: moment().endOf('year').format("YYYY-MM-DD"),
        }
        break;
      case "lastMonth":
        title = <FormattedMessage id="bookings.filters.lastMonth" />
        range = {
          startDate: moment().add(-1,'month').startOf('month').format("YYYY-MM-DD"),
          endDate: moment().add(-1,'month').endOf('month').format("YYYY-MM-DD"),
        }
        break;
      case "month":
        title = <FormattedMessage id="bookings.filters.thisMonth" />
        range = {
          startDate: moment().startOf('month').format("YYYY-MM-DD"),
          endDate: moment().endOf('month').format("YYYY-MM-DD"),
        }
        break;
      case "lastWeek":
        title = <FormattedMessage id="bookings.filters.lastWeek" />
        range = {
          startDate: moment().add(-1, 'week').startOf('week').format("YYYY-MM-DD"),
          endDate: moment().add(-1, 'week').endOf('week').format("YYYY-MM-DD")
        }
        break;
      case "week":
        title = <FormattedMessage id="bookings.filters.thisWeek" />
        range = {
          startDate: moment().startOf('week').format("YYYY-MM-DD"),
          endDate: moment().endOf('week').format("YYYY-MM-DD")
        }
        break;
      case "yesterday":
        title = <FormattedMessage id="bookings.filters.yesterday" />
        range = {
          startDate: moment().add(-1, 'days').format("YYYY-MM-DD"),
          endDate: moment().add(-1, 'days').format("YYYY-MM-DD")
        }
        break;
      case "today":
        title = <FormattedMessage id="bookings.filters.today" />
        const today = moment();
        range = {
          startDate: today.format("YYYY-MM-DD"),
          endDate: today.format("YYYY-MM-DD")
        }
        break;
        case "yearToDate":
          title = <FormattedMessage id="bookings.filters.today" />
          range = {
            startDate: moment().startOf('year').format("YYYY-MM-DD"),
            endDate:  moment().format("YYYY-MM-DD")
          }
          break;
      }

      if(id === "booking") {
        setTypeOption({
          ...gridOptions,
          bookingStartDate: range.startDate ? range.startDate : "",
          bookingEndDate: range.endDate ? range.endDate : "",
          page: 1
        })
      } else if (id === "booked"){
        setTypeOption({
          ...gridOptions,
          bookedStartDate: range.startDate ? range.startDate : "",
          bookedEndDate: range.endDate ? range.endDate : "",
          page: 1
        })
      }
      else if(id === "travel") {
        setTypeOption({
          ...gridOptions,
          travelStartDate: range.startDate ? range.startDate : "",
          travelEndDate: range.endDate ? range.endDate : "",
          page: 1
        })
      }
      setAppliedPlaceholder(title)
      setType(type)
      setStartDate(range.startDate)
      setEndDate(range.endDate)
      setStartDateInitial(range.startDate)
      setEndDateInitial(range.endDate)
      document.getElementById("selected-dates").focus()
  }

  const applyDates = () => {
    if(type === "custom") {
      if(id === "booking") {
        setGridOptions({
          ...gridOptions,
          bookingStartDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
          bookingEndDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
          page: 1
        })
        setStartDateInitial(startDate)
        setEndDateInitial(endDate)
        if (moment(startDate).isSame(endDate)){
          setPlaceholder(`${moment(startDate).locale(globalLocale).format(dateFormat)}`);
        } else {
          setPlaceholder(`${moment(startDate).locale(globalLocale).format(dateFormat)} - ${moment(endDate).locale(globalLocale).format(dateFormat)}`);
        }
      }
      if(id === "booked") {
        setGridOptions({
          ...gridOptions,
          bookedStartDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
          bookedEndDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
          page: 1
        })
        setStartDateInitial(startDate)
        setEndDateInitial(endDate)
        if (moment(startDate).isSame(endDate)){
          setPlaceholder(`${moment(startDate).locale(globalLocale).format(dateFormat)}`);
        } else {
          setPlaceholder(`${moment(startDate).locale(globalLocale).format(dateFormat)} - ${moment(endDate).locale(globalLocale).format(dateFormat)}`);
        }
      }
      if(id === "travel") {
        setGridOptions({
          ...gridOptions,
          travelStartDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
          travelEndDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
          page: 1
        })
        setStartDateInitial(startDate)
        setEndDateInitial(endDate)
        if (moment(startDate).isSame(endDate)){
          setPlaceholder(`${moment(startDate).locale(globalLocale).format(dateFormat)}`);
        } else {
          setPlaceholder(`${moment(startDate).locale(globalLocale).format(dateFormat)} - ${moment(endDate).locale(globalLocale).format(dateFormat)}`);
        }
      }
    } else {
      setGridOptions(typeOption)
      setPlaceholder(appliedPlaceholder)
    }
    setShowOverlay(false)
  }

  const cancelSelection = () => {
    setStartDate(startDateInitial);
    setEndDate(endDateInitial);
    setShowOverlay(false)
  }

  const renderPopover = (e) => {
    return(
        <Popover className={classes.popoverStyle} tabIndex={0} aria-label={formatMessage(messages.popoverAriaLabel)}>
          <UCOutsideClickHandler onOutsideClick = {() => cancelSelection()}>
            <div className={classes.popoverDiv}>
              <div className={classes.popoverContainer}>
                <div className={classes.buttonContainer}>
                  <UCButton
                    type="default"
                    onClick={() => applyButtonDates('today')}
                    className={`${classes.filter} ${classes.buttonStyle} ${type === "today" ? classes.active : ""}`}
                    text={<FormattedMessage id="safety.filters.today" />}
                    tabIndex={0}
                  />
                  <UCButton
                    onClick={() => applyButtonDates('yesterday')}
                    type="default"
                    className={`${classes.filter} ${classes.buttonStyle} ${type === "yesterday" ? classes.active : ""}`}
                    text={<FormattedMessage id="safety.filters.yesterday" />}
                    tabIndex={0}
                  />
                  <UCButton
                    onClick={() => applyButtonDates('week')}
                    type="default"
                    className={`${classes.filter} ${classes.buttonStyle} ${type === "week" ? classes.active : ""}`}
                    text={<FormattedMessage id="safety.filters.thisWeek" />}
                    tabIndex={0}
                  />
                  <UCButton
                    onClick={() => applyButtonDates('lastWeek')}
                    type="default"
                    className={`${classes.filter} ${classes.buttonStyle} ${type === "lastWeek" ? classes.active : ""}`}
                    text={<FormattedMessage id="bookings.filters.lastWeek" />}
                    tabIndex={0}
                  />
                  <UCButton
                    onClick={() => applyButtonDates('month')}
                    type="default"
                    className={`${classes.filter} ${classes.buttonStyle} ${type === "month" ? classes.active : ""}`}
                    text={<FormattedMessage id="safety.filters.thisMonth" />}
                    tabIndex={0}
                  />
                  <UCButton
                    onClick={() => applyButtonDates('lastMonth')}
                    type="default"
                    className={`${classes.filter} ${classes.buttonStyle} ${type === "lastMonth" ? classes.active : ""}`}
                    text={<FormattedMessage id="bookings.filters.lastMonth" />}
                    tabIndex={0}
                  />
                  <UCButton
                    onClick={() => applyButtonDates('year')}
                    type="default"
                    className={`${classes.filter} ${classes.buttonStyle} ${type === "year" ? classes.active : ""}`}
                    text={<FormattedMessage id="bookings.filters.year" />}
                    tabIndex={0}
                  />
                  <UCButton
                    onClick={() => applyButtonDates('lastYear')}
                    type="default"
                    className={`${classes.filter} ${classes.buttonStyle} ${type === "lastYear" ? classes.active : ""}`}
                    text={<FormattedMessage id="bookings.filters.lastYear" />}
                    tabIndex={0}
                  />
                  <UCButton
                    onClick={() => applyButtonDates('allTime')}
                    type="default"
                    className={`${classes.filter} ${classes.buttonStyle} ${type === "allTime" ? classes.active : ""}`}
                    text={<FormattedMessage id="bookings.filters.allTime" />}
                    tabIndex={0}
                  />
                </div>
                <div>
                  <div className={classes.flat}>
                    <UCSelect
                      styles={dateSelectOpen}
                      className={classes.select}
                      defaultValue={{ value: defaultValue, label: defaultText}}
                      options={selectCalendarMobileOptions}
                      onChange={(tab) => applyButtonDates(tab.value)}
                      ariaLabel={defaultText}
                    />
                    <Flatpickr
                      options={{
                        mode: "range",
                        inline: true,
                        dateFormat: "Y-m-d",
                        showMonths: isMobile ? "1" : "2",
                        defaultDate: [startDate, endDate],
                        locale: {
                          ...getCalendarLocale(globalLocale),
                          firstDayOfWeek: 1 // start week on Monday
                        },
                        onReady: (selectedDates, dateStr, instance) => {
                          const calendar = instance.calendarContainer;
                          const prevMonthNav = instance.prevMonthNav;
                          const nextMonthNav = instance.nextMonthNav;

                          calendar.setAttribute("tabIndex", "0");
                          prevMonthNav.setAttribute("aria-label", formatMessage(messages.prevBtnAriaLabel));
                          nextMonthNav.setAttribute("aria-label", formatMessage(messages.nextBtnAriaLabel));
                        },
                      }}
                      onChange={(date) => {
                        setStartDate(date[0])
                        setEndDate(date[1] ? date[1] : null)
                        setType("custom")
                        setTypeOption({})
                        document.getElementById("selected-dates").focus()
                      }}
                    />
                  </div>
                  <Row className={classes.footer}>
                    <Col xs={12} sm={12} md={6} lg={6} className={classes.selectedDates} id="selected-dates" tabIndex={startDate ? 0 : -1}>
                      {
                        startDate &&
                        <>
                          <div className={classes.selectedDate}>
                            { startDate && moment(startDate).locale(globalLocale).format(dateFormat) }
                          </div>
                          <div className={classes.separator}>
                            -
                          </div>
                          <div className={classes.selectedDate}>
                            { endDate && moment(endDate).locale(globalLocale).format(dateFormat) }
                          </div>
                        </>
                      }
                    </Col>
                    <Col xs={12} sm={12} md={6} lg={6} className={classes.actions}>
                      <UCButton
                        onClick={() => {
                          setStartDate(gridOptions?.startDate && gridOptions?.type === "custom" ? gridOptions?.startDate : null);
                          setEndDate(gridOptions?.endDate && gridOptions?.type === "custom" ? gridOptions?.endDate : null);
                          setType(defaultValue);
                          setShowOverlay(false);
                        }}
                        type="default"
                        className={classes.cancel}
                        text={<FormattedMessage id="safety.filters.cancel" />}
                        tabIndex={0}
                      />
                      <UCButton
                        onClick={(e) => applyDates()}
                        type="primary"
                        disabled={!(startDate && endDate) && type !=="allTime"}
                        className={classes.apply}
                        text={<FormattedMessage id="safety.filters.apply" />}
                        tabIndex={0}
                      />
                    </Col>
                  </Row>
                </div>
              </div>

            </div>
          </UCOutsideClickHandler>
      </Popover>
    )
  }
  return (

    <OverlayTrigger show={showOverlay} trigger="click" rootClose={true} placement="bottom-start" overlay={renderPopover()} onEntered={(node) => node.focus()}>
      <div>
        <UCButton
          onClick={() => setShowOverlay(true)}
          type="default"
          className={`${classes.filter} ${classes.selectDates} ${gridOptions?.type === "custom" ? classes.active : ""}`}
          text={placeholder}
          ariaLabel={defaultText}
          tabIndex={0}
          icon={
            <CalendarIcon />
          }
        />
      </div>
    </OverlayTrigger>
  )
}

export default SelectBookingDates;
